// =============================================================================
// TestSpec — front-end-authored, versioned, signable Voice-Test case
// =============================================================================
// Append-only, content-addressed: `specKey` = stable logical id; each content
// change writes a NEW immutable doc (version+1) and flips the old head's isLatest.
// `specHash` = sha256 of canonical content (excludes version/timestamps/signoff).
// Sign-off is append-only + hash-bound (an edit voids prior approval).
// Complex nested shapes stored as JSON strings (repo convention).
// =============================================================================

import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection } from '@adornis/baseql/server/collections.js';

export interface ScriptedCallerTurn { index: number; text: string; waitForAgent?: string; delayMs?: number; }
export interface AcceptanceRule { id: string; kind: string; target: string; label: string; hard?: boolean; }
export interface OracleConfig { enabled: boolean; rubric?: string; minScore?: number; model?: string; }
export interface NonFunctionalThresholds { maxCallMs?: number; maxMeanTurnLatencyMs?: number; maxCostEUR?: number; maxTurns?: number; }
export interface TestSpecSignOff { byUserID: string; byName?: string; atISO: string; specHash: string; version: number; note?: string; }

@Entity()
export class TestSpec extends MongoEntity {
  static override _class = 'TestSpec';
  static override _collectionName = 'test_specs';
  constructor(data: PartialEntityData<TestSpec>) { super(data); }

  @Field(() => String) specKey: Maybe<string>;
  @Field(() => Int) version: Maybe<number>;
  @Field(() => Boolean) isLatest: Maybe<boolean>;
  @Field(() => String) specHash: Maybe<string>;
  @Field(() => String) name: Maybe<string>;
  @Field(() => String) description: Maybe<string>;
  @Field(() => String) personaProfileId: Maybe<string>;
  @Field(() => String) personaLang: Maybe<'de' | 'en'>;
  @Field(() => String) scriptedCallerTurnsJSON: Maybe<string>;
  @Field(() => String) expectedFlow: Maybe<string>;
  @Field(() => String) acceptanceRulesJSON: Maybe<string>;
  @Field(() => String) oracleConfigJSON: Maybe<string>;
  @Field(() => String) nonFunctionalThresholdsJSON: Maybe<string>;
  @Field(() => Int) repetitions: Maybe<number>;
  @Field(() => Float) passThreshold: Maybe<number>;
  @Field(() => String) signOffsJSON: Maybe<string>;
  @Field(() => String) createdByUserID: Maybe<string>;
  @Field(() => Date) createdAt: Maybe<Date>;
  @Field(() => Date) versionedAt: Maybe<Date>;
}

// ─── unique indexes (run at boot via getRawCollection-backed getCollection in serverContext) ──
export async function ensureTestSpecIndexes(): Promise<void> {
  const collection = await getCollection<TestSpec>(TestSpec._class); // raw at boot (serverContext)
  await collection.createIndex({ specKey: 1, isLatest: 1 }, { name: 'specKey_isLatest_unique', unique: true, partialFilterExpression: { isLatest: true } });
  await collection.createIndex({ specKey: 1, version: 1 }, { name: 'specKey_version_unique', unique: true });
}
